import { useState, useMemo } from 'react'
import DismissButton from 'components/dismiss-button'
import { BlueButton } from 'components/blue-button'
import { Lesson, Class } from '@all-for-one/shared-types'
import { AnswerKeyPdfDocument } from './pdf-documents'
import { getLogoPngDataUrl, getPdfDownloadDate, downloadPdf } from 'util/pdf-download'
import printStyles from 'css/components/lesson-builder/print-overlay.module.css'

interface PrintAnswerKeyOverlayProps {
    lesson: Lesson
    className?: string
    gradeBand?: string
    subject?: string
    classData?: Class
    onDismiss: () => void
}

const SECTION_MAP: Record<string, string> = {
    assessment: 'Pre/Post Assessment',
    cfu: 'Check for Understanding',
    iw: 'Independent Work',
}

const SORT_LABELS: Record<string, string> = {
    'section-level': 'Section \u2192 Level',
    'level-section': 'Level \u2192 Section',
}

export const PrintAnswerKeyOverlay = ({
    lesson,
    className: assignedClassName,
    gradeBand,
    subject,
    classData,
    onDismiss,
}: PrintAnswerKeyOverlayProps) => {
    const [step, setStep] = useState(1)
    const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set())
    const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set())
    const [sortOrder, setSortOrder] = useState<'section-level' | 'level-section'>('section-level')
    const [downloading, setDownloading] = useState(false)

    const toggleSection = (key: string) => {
        setSelectedSections(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleLevel = (level: string) => {
        setSelectedLevels(prev => {
            const next = new Set(prev)
            if (next.has(level)) next.delete(level)
            else next.add(level)
            return next
        })
    }

    const studentCounts = useMemo(() => {
        if (!classData?.students) return null
        return {
            Emerging: classData.students.filter(s => s.difficulty === 'Emerging').length,
            Intermediate: classData.students.filter(s => s.difficulty === 'Intermediate').length,
            Advanced: classData.students.filter(s => s.difficulty === 'Advanced').length,
        }
    }, [classData])

    const canAdvanceFromStep1 = selectedSections.size > 0
    const canAdvanceFromStep2 = selectedLevels.size > 0

    const handleDownload = async () => {
        setDownloading(true)
        try {
            const logoDataUrl = await getLogoPngDataUrl()
            const doc = (
                <AnswerKeyPdfDocument
                    lesson={lesson}
                    assignedClassName={assignedClassName}
                    gradeBand={gradeBand}
                    subject={subject}
                    logoDataUrl={logoDataUrl}
                    downloadDate={getPdfDownloadDate()}
                    selectedSections={selectedSections}
                    selectedLevels={selectedLevels}
                    sortOrder={sortOrder}
                />
            )
            await downloadPdf(doc, `${lesson.name}_answer_key.pdf`)
            onDismiss()
        } finally {
            setDownloading(false)
        }
    }

    const summaryText = useMemo(() => {
        const sectionNames = ['assessment', 'cfu', 'iw']
            .filter(k => selectedSections.has(k))
            .map(k => SECTION_MAP[k])
            .join(' + ')
        const levelNames = ['Emerging', 'Intermediate', 'Advanced']
            .filter(l => selectedLevels.has(l))
            .join(' + ')
        return `You are downloading: ${sectionNames} \u00b7 ${levelNames} \u00b7 Sort: ${SORT_LABELS[sortOrder]}`
    }, [selectedSections, selectedLevels, sortOrder])

    return (
        <div className="overlay">
            <div className="overlay-content-container">
                <div className="overlay-content-header">
                    Download Answer Key
                    <DismissButton onClick={onDismiss} />
                </div>
                <div className="overlay-content-body">
                    {step === 1 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Which sections do you want to include?</h3>
                            {(['assessment', 'cfu', 'iw'] as const).map(key => (
                                <label key={key} className={printStyles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={selectedSections.has(key)}
                                        onChange={() => toggleSection(key)}
                                    />
                                    {SECTION_MAP[key]}
                                </label>
                            ))}
                            <div className={printStyles.navRow}>
                                <div />
                                <BlueButton label="Next" onClick={() => setStep(2)} disabled={!canAdvanceFromStep1} />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Which levels do you want to include?</h3>
                            {(['Emerging', 'Intermediate', 'Advanced'] as const).map(level => (
                                <label key={level} className={printStyles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={selectedLevels.has(level)}
                                        onChange={() => toggleLevel(level)}
                                    />
                                    {level}{studentCounts ? ` (${studentCounts[level]} students)` : ''}
                                </label>
                            ))}
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(1)}>Back</button>
                                <BlueButton label="Next" onClick={() => setStep(3)} disabled={!canAdvanceFromStep2} />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>How should questions be sorted?</h3>
                            <label className={printStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="sortOrder"
                                    checked={sortOrder === 'section-level'}
                                    onChange={() => setSortOrder('section-level')}
                                />
                                Section &rarr; Level
                            </label>
                            <label className={printStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="sortOrder"
                                    checked={sortOrder === 'level-section'}
                                    onChange={() => setSortOrder('level-section')}
                                />
                                Level &rarr; Section
                            </label>
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(2)}>Back</button>
                                <BlueButton label="Next" onClick={() => setStep(4)} />
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className={printStyles.stepContent}>
                            <h3 className={printStyles.stepTitle}>Summary</h3>
                            <p className={printStyles.summaryText}>{summaryText}</p>
                            <div className={printStyles.navRow}>
                                <button className={printStyles.secondaryBtn} onClick={() => setStep(3)}>Back</button>
                                <BlueButton label={downloading ? 'Generating…' : 'Download'} onClick={handleDownload} disabled={downloading} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
